import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { getStackedBarColors } from 'src/components/molecules/BarStacked';
import {
  LocalFilterOptions,
  ResetFilterOptions,
  SaveFilterParams,
} from 'src/components/organisms';
import { TimeframeInput, useGetCasesPerWeekBarStackedQuery } from 'src/graphql';
import * as Types from 'src/types';
import { InsightsDashboard, LocationFilterCategory, State } from 'src/types';
import { sortDataArrayByDayRange, sortDataArrayByNumber } from 'src/utils';
import { CHART_IDS, trackChartRedrawn } from 'src/utils/analytics';
import { buildLocationQueryVariables } from 'src/utils/insights/buildLocationQueryVariables';
import { getYear } from 'src/utils/time';
import { getColumnNames } from '../helpers';
import { useTheme } from 'styled-components';

export type TableDataType = {
  cellData: (string | number | null | undefined)[];
  row: string;
};

export type ColumnSortType = {
  columnName: string;
  direction: 'asc' | 'desc';
};
interface Parameters {
  globalFilterSites: string[];
  globalFilterCountries: string[];
  selectedCaseCountryCodes: string[];
  selectedCaseRegionIds: string[];
  globalFilterTimeframe: TimeframeInput;
  organisationData: {
    sites?: string[];
    countries?: string[];
    departments?: string[];
    oldestReportSubmittedAtDate: Date;
    categories?: string[];
  };
}

const tableColumns = [
  { label: 'Duration (days)' },
  { label: 'Cases open' },
  { label: 'Cases closed' },
  { label: 'Total cases' },
];

export const useCasesOverTime = ({
  globalFilterSites,
  globalFilterCountries,
  selectedCaseCountryCodes,
  selectedCaseRegionIds,
  globalFilterTimeframe,
  organisationData,
}: Parameters) => {
  const theme = useTheme();
  const stackedBarColors = getStackedBarColors(theme);
  const storedGlobalFilters = useSelector(
    (state: State) =>
      state.insightsGlobalFilters[InsightsDashboard.OPERATIONAL],
  );
  const currentGlobalLocationFilter =
    storedGlobalFilters.currentGlobalLocationFilter;
  const [selectedDepartments, setSelectedDepartments] = useState(
    organisationData.departments ?? [],
  );
  const [selectedCategories, setSelectedCategories] = useState(
    organisationData.categories ?? [],
  );
  const [pdfIsDownloading, setPdfIsDownloading] = useState<boolean>(false);
  const [timeframe, setTimeframe] = useState(globalFilterTimeframe);
  const [showOpenCases, setShowOpenCases] = useState(true);
  const [showClosedCases, setShowClosedCases] = useState(true);
  const [selectedSites, setSelectedSites] = useState(globalFilterSites);
  const [selectedCountries, setSelectedCountries] = useState(
    globalFilterCountries,
  );
  const [localLocationFilter, setLocalLocationFilter] = useState(
    currentGlobalLocationFilter,
  );
  const [displayTableData, setDisplayTableData] = useState<TableDataType[]>([]);
  const [storedTableData, setStoredTableData] = useState<TableDataType[]>([]);
  const [sortedColumn, setSortedColumn] = useState<ColumnSortType>({
    columnName: 'Duration (days)',
    direction: 'asc',
  });
  const queryLocationVariables = buildLocationQueryVariables({
    locationFilter: localLocationFilter,
    locationFilterCategory: storedGlobalFilters.locationFilterCategory,
    sites: selectedSites,
    countries: selectedCountries,
    caseCountryCodes: selectedCaseCountryCodes,
    caseRegionIds: selectedCaseRegionIds,
  });

  const { data: barStackedData, isLoading } = useGetCasesPerWeekBarStackedQuery(
    {
      ...queryLocationVariables,
      casesType: { open: showOpenCases, closed: showClosedCases },
      timeframe,
      departments: selectedDepartments,
      categories: selectedCategories,
    },
  );
  const organisation = useSelector((state: Types.State) => state.organisation);
  const features = useSelector((state: Types.State) => state.features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  useEffect(() => {
    setSelectedSites(globalFilterSites);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterSites, currentGlobalLocationFilter]);

  useEffect(() => {
    setSelectedCountries(globalFilterCountries);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterCountries, currentGlobalLocationFilter]);

  useEffect(() => {
    setTimeframe(globalFilterTimeframe);
  }, [globalFilterTimeframe]);

  useEffect(() => {
    if (!selectedDepartments.length && organisationData.departments?.length) {
      setSelectedDepartments(organisationData.departments);
    }
  }, [organisationData.departments, selectedDepartments.length]);

  useEffect(() => {
    if (!selectedCategories.length && organisationData.categories?.length) {
      setSelectedCategories(organisationData.categories);
    }
  }, [organisationData.categories, selectedCategories.length]);

  const isCaseMode =
    storedGlobalFilters.locationFilterCategory === LocationFilterCategory.CASE;

  const localFilterOptions: LocalFilterOptions = {
    ...(isCaseMode
      ? {}
      : {
          site: {
            options: organisationData.sites ?? [],
            selectedOptions: selectedSites,
          },
          country: {
            options: organisationData.countries ?? [],
            selectedOptions: selectedCountries,
          },
          selectedLocationFilter: localLocationFilter,
        }),
    time: {
      timeframe,
      oldestSelectableYear: getYear(
        organisationData.oldestReportSubmittedAtDate,
      ),
    },
    department: {
      options: organisationData.departments ?? [],
      selectedOptions: selectedDepartments,
    },
    category: {
      options: organisationData.categories ?? [],
      selectedOptions: selectedCategories,
    },
  };

  const stackedBarLabels = barStackedData
    ? barStackedData?.getCasesPerWeekBarStacked.stackLabels
    : [];

  type caseStatusType = 'open' | 'closed';

  useEffect(() => {
    const tableData =
      barStackedData?.getCasesPerWeekBarStacked.data.map((data) => {
        const startDay = parseInt(data.bar, 10);
        const dayRange =
          startDay === 98 ? '98+' : `${startDay}-${startDay + 6}`;
        return {
          cellData: [dayRange, data.open, data.closed, data.total],
          row: dayRange,
        };
      }) ?? [];

    if (!isEqual(tableData, storedTableData)) {
      setStoredTableData(tableData);
    }
  }, [barStackedData, storedTableData]);

  const handleColumnSort = useCallback(
    (columnName: string, direction: 'asc' | 'desc') => {
      const columnIndex = getColumnNames(tableColumns).indexOf(columnName);
      setSortedColumn({ columnName, direction });
      switch (columnName) {
        case 'Duration (days)': {
          const newTableData = sortDataArrayByDayRange(
            storedTableData,
            direction,
          );
          setDisplayTableData(newTableData);
          break;
        }
        case 'Cases open':
        case 'Cases closed':
        case 'Total cases': {
          const newTableData = sortDataArrayByNumber(
            storedTableData,
            columnIndex,
            direction,
          );
          setDisplayTableData(newTableData);
          break;
        }
        default: {
          setDisplayTableData(storedTableData);
          break;
        }
      }
    },
    [storedTableData],
  );

  useEffect(() => {
    handleColumnSort(sortedColumn.columnName, sortedColumn.direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedColumn.columnName, sortedColumn.direction, storedTableData]);

  const [activeStackedBarLegendItems, setActiveStackedBarLegendItems] =
    useState({
      open: true,
      closed: true,
    });

  const activeCount = (legendItems: { open: boolean; closed: boolean }) =>
    Object.values(legendItems).filter((value) => value === true).length;

  const onStackedBarLegendClick = (item: string) => {
    if (organisation.organisation && segmentEnabled) {
      trackChartRedrawn(
        organisation.organisation.id,
        'operational',
        CHART_IDS.case_time_to_resolution,
      );
    }
    if (item.toLowerCase() === 'closed') {
      const newActiveStackedBarLegendItems = {
        ...activeStackedBarLegendItems,
        closed: !activeStackedBarLegendItems.closed,
      };

      if (activeCount(newActiveStackedBarLegendItems) > 0) {
        setActiveStackedBarLegendItems(newActiveStackedBarLegendItems);
      }
    }

    if (item.toLowerCase() === 'open') {
      const newActiveStackedBarLegendItems = {
        ...activeStackedBarLegendItems,
        open: !activeStackedBarLegendItems.open,
      };

      if (activeCount(newActiveStackedBarLegendItems) > 0) {
        setActiveStackedBarLegendItems(newActiveStackedBarLegendItems);
      }
    }
  };
  const saveLocalFilters = (params: SaveFilterParams) => {
    if (params.selectedSites) {
      setSelectedSites(params.selectedSites);
    }
    if (params.selectedCountries) {
      setSelectedCountries(params.selectedCountries);
    }
    if (params.selectedDepartments) {
      setSelectedDepartments(params.selectedDepartments);
    }
    if (params.selectedTimeframe) {
      setTimeframe(params.selectedTimeframe);
    }
    if (params.selectedCategories) {
      setSelectedCategories(params.selectedCategories);
    }
    if (params.selectedLocationFilter) {
      setLocalLocationFilter(params.selectedLocationFilter);
    }
  };

  useEffect(() => {
    setShowOpenCases(activeStackedBarLegendItems.open);
    setShowClosedCases(activeStackedBarLegendItems.closed);
  }, [activeStackedBarLegendItems]);

  useEffect(() => {
    setActiveStackedBarLegendItems({ open: true, closed: true });
  }, [
    timeframe,
    selectedCategories,
    selectedDepartments,
    selectedSites,
    selectedCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
  ]);

  const timeToResolutionLegend = stackedBarLabels.map((label, i) => {
    return {
      text: label,
      color: stackedBarColors[i],
      active: activeStackedBarLegendItems[label as caseStatusType],
    };
  });

  const resetLocalFilters: ResetFilterOptions = {
    siteOptions: globalFilterSites,
    countryOptions: globalFilterCountries,
    departmentOptions: organisationData.departments ?? [],
    timeframe: globalFilterTimeframe,
    categoryOptions: organisationData.categories ?? [],
    globalFilterLocation: currentGlobalLocationFilter,
  };

  return {
    barStackedData,
    timeToResolutionLegend,
    isLoading,
    onStackedBarLegendClick,
    localFilterOptions,
    saveLocalFilters,
    casesOverTime: barStackedData?.getCasesPerWeekBarStacked.data,
    tableColumns,
    tableData: displayTableData,
    handleColumnSort,
    sortedColumn,
    pdfIsDownloading,
    setPdfIsDownloading,
    localLocationFilter,
    resetLocalFilters,
  };
};
